import React, { FC, useContext, useEffect } from 'react';
import { Typography, theme, Popover } from 'antd';
import { HolderOutlined, InfoCircleOutlined, FormOutlined, TeamOutlined, EyeOutlined, TableOutlined, SettingOutlined, ProfileOutlined, BlockOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable, useDndContext, useDraggable } from '@dnd-kit/core';
import {
    UIElementType,
    UIField,
    UIFormProps,
    UIArTable,
    UIButton,
    EditorUIElement,
    EditorUIContainer
} from '#types/System.types';
import { LayoutEditorContext } from '../../providers/editor/LayoutEditorContext';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import { LayoutEditorControls } from './LayoutEditorControls';
import { useKeybindings } from '@/keyboard';
import { useLayoutEditorStyles } from './element/LayoutEditorElement.styles';
import { CONTAINER_ELEMENT_TYPES, workflowMenuSectionLabels } from '#types/System.Parameters.types';
import { UIElementTypeDisplayNames } from '#types/System.Themes.types';

const { Text } = Typography;
const { useToken } = theme;

/**
 * @description Data associated with draggable/sortable/droppable elements for dnd-kit.
 * Contains properties relevant to both the dragged item and the drop target.
 */
export interface DndElementData {
    /** The type of the UI element being dragged or the element associated with the droppable */
    type?: UIElementType;
    /** The dnd ID of the parent container element, if any ('e0' if root) */
    container?: string | null;
    /** The numerical index of the element within its parent container */
    index?: number;
    /** Flag indicating if the element (draggable or droppable's owner) is a container */
    isContainer?: boolean;
    /** The UIElementType of the parent container, if any */
    parentType?: UIElementType | null;
    /** The specific dnd ID of the target element for placement (used by before/after droppables) */
    targetElementId?: string;
    /** Indicates the intended drop placement relative to the targetElementId ('before', 'inside', 'after') */
    placement?: 'before' | 'inside' | 'after';
    /** The dnd ID of the logical parent where the drop occurs (relevant for before/after/inside) */
    parentId?: string;
    /** The orientation of the parent container ('HORIZONTAL' or 'VERTICAL') */
    parentOrientation?: 'HORIZONTAL' | 'VERTICAL';
}

/**
 * Props for the LayoutEditorElement component
 */
interface LayoutEditorElementProps {
    /** The UI element to render */
    element: EditorUIElement;
    /** The parent element containing this element, null if root */
    parentElement: EditorUIElement | null;
    /** Whether this element is currently selected */
    isSelected?: boolean;
    /** Callback when this element is selected */
    onSelect?: () => void;
    /** Additional CSS styles to apply */
    style?: React.CSSProperties;
}

/**
 * A draggable and configurable UI element in the layout editor.
 * Renders itself and manages its own droppable zones for before/after placement.
 *
 * @returns {React.ReactElement} The rendered LayoutEditorElement component.
 */
export const LayoutEditorElement: FC<LayoutEditorElementProps> = ({
    element,
    parentElement,
    isSelected,
    onSelect,
    style,
}) => {
    const { token } = useToken();
    const editor = useContext(LayoutEditorContext);
    const { fieldTypes } = useContext(FieldTypeContext);
    const { over } = useDndContext();
    if (!editor) throw new Error('Editor must be used within LayoutEditorProvider');

    const {
        removeElement,
        tableTypes,
        selectElement
    } = editor;

    const { onKeyDown: onActivateKey } = useKeybindings({
        'editor.activate': () => onSelect?.(),
    });

    // Check if element is a container using the centralized constant
    const isContainer = CONTAINER_ELEMENT_TYPES.includes(element.uiElementType);
    
    const isColumn = element.uiElementType === UIElementType.Column;
    const {
        getElementStyle,
        getTypeTextStyle,
        getMetaIconStyle,
        getDragHandleStyle
    } = useLayoutEditorStyles();

    /**
     * Determines the orientation of the parent container.
     */
    const parentOrientation: 'HORIZONTAL' | 'VERTICAL' =
        parentElement?.uiElementType === UIElementType.Row ? 'HORIZONTAL' : 'VERTICAL';

    useEffect(() => {
        /**
         * Handles keyboard events for the selected element.
         * @param {KeyboardEvent} e - The keyboard event.
         */
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isSelected) return;
            const target = e.target as HTMLElement | null;
            if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;

            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                if (parentElement) {
                    removeElement(parentElement as EditorUIContainer, element.dndId);
                }
            } else if (e.key === 'Escape') {
                selectElement(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [editor, isSelected, parentElement, element.dndId, removeElement, selectElement]);

    /**
     * The index of the current element within its parent's children array.
     */
    const currentIndex = parentElement ? (parentElement.children || []).findIndex(child => (child as EditorUIElement).dndId === element.dndId) : -1;

    /**
     * dnd-kit sortable hook configuration. Enabled only for container elements.
     */
    const sortable = useSortable({
        id: element.dndId,
        data: {
            type: element.uiElementType,
            container: parentElement?.dndId || 'e0',
            index: currentIndex,
            isContainer,
            parentType: parentElement?.uiElementType,
        } satisfies DndElementData,
        disabled: parentElement === null || !isContainer,
    });
    /**
     * dnd-kit draggable hook configuration. Enabled only for non-container elements.
     */
    const draggable = useDraggable({
        id: element.dndId,
        data: {
            type: element.uiElementType,
            container: parentElement?.dndId || 'e0',
            index: currentIndex,
            isContainer,
            parentType: parentElement?.uiElementType,
        } satisfies DndElementData,
        disabled: isContainer,
    });

    /**
     * dnd-kit droppable hook for dropping items *inside* a container element.
     */
    const insideDroppable = useDroppable({
        id: element.dndId,
        data: {
            targetElementId: element.dndId,
            placement: 'inside',
            parentId: parentElement?.dndId || 'e0',
            parentOrientation,
            index: currentIndex,
            isContainer,
            parentType: parentElement?.uiElementType,
            type: element.uiElementType,
        } satisfies DndElementData,
        disabled: !isContainer,
    });
    /**
     * dnd-kit droppable hook for dropping items *before* an element.
     */
    const beforeDroppable = useDroppable({
        id: `${element.dndId}-before`,
        data: {
            targetElementId: element.dndId,
            placement: 'before',
            parentId: parentElement?.dndId || 'e0',
            parentOrientation,
            index: currentIndex,
            isContainer,
            parentType: parentElement?.uiElementType,
            type: element.uiElementType,
        } satisfies DndElementData,
    });
    /**
     * dnd-kit droppable hook for dropping items *after* an element.
     */
    const afterDroppable = useDroppable({
        id: `${element.dndId}-after`,
        data: {
            targetElementId: element.dndId,
            placement: 'after',
            parentId: parentElement?.dndId || 'e0',
            parentOrientation,
            index: currentIndex !== -1 ? currentIndex + 1 : 0,
            isContainer,
            parentType: parentElement?.uiElementType,
            type: element.uiElementType,
        } satisfies DndElementData,
    });

    const insideRef = insideDroppable.setNodeRef;
    const beforeRef = beforeDroppable.setNodeRef;
    const afterRef = afterDroppable.setNodeRef;

    /**
     * Combined ref callback for dnd-kit node assignment.
     * Assigns the node to draggable for context interaction,
     * and conditionally to insideDroppable for containers.
     * @param {HTMLElement | null} el - The DOM element node.
     */
    const setNodeRef = (el: HTMLElement | null) => {
        draggable.setNodeRef(el);
        if (isContainer) insideRef(el);
    };

    const attributes = (((isContainer ? sortable.attributes : draggable.attributes) || {}));
    const listeners  = (((isContainer ? sortable.listeners  : draggable.listeners )   || {}));
    const isDragging = isContainer ? sortable.isDragging : draggable.isDragging;

    const type = element.uiElementType === UIElementType.Field
        ? fieldTypes[(element as UIField).fieldId]?.fieldDataTypeId
        : element.uiElementType === UIElementType.MetaField
            ? 'meta'
            : element.uiElementType === UIElementType.FormProperty
                ? 'text'
                : element.uiElementType === UIElementType.Attachments
                    ? 'file'
                    : element.uiElementType === UIElementType.ReferencedList
                        ? 'reference list'
                        : element.uiElementType === UIElementType.Table
                            ? 'table'
                            : element.uiElementType === UIElementType.Button
                                ? 'button'
                                : 'entity';

    /**
     * Computes the style for the main element div.
     */
    const elementStyle = getElementStyle({
        isContainer,
        isColumn,
        isSelected: !!isSelected,
        isMeta: element.uiElementType === UIElementType.MetaField,
        isField: element.uiElementType === UIElementType.Field,
        isFormProp: element.uiElementType === UIElementType.FormProperty,
        isAttachments: element.uiElementType === UIElementType.Attachments
            || element.uiElementType === UIElementType.ReferencedList,
        style,
        elementProps: 'props' in element ? element.props : undefined,
    });

    /**
     * Renders the children of a container element using SortableContext.
     * @returns {React.ReactNode} The rendered children or undefined if not a container.
     */
    const renderChildren = () => {
        if (isContainer) {
            const container = element as EditorUIElement;
            const childIds = container.children?.map((child: EditorUIElement) => child.dndId) || [];

            return (
                <SortableContext
                    items={childIds}
                    strategy={rectSortingStrategy}
                >
                    {container.children?.map((child) => (
                        <LayoutEditorElement
                            key={child.dndId}
                            element={child}
                            parentElement={container}
                            isSelected={editor.selectedElement?.dndId === child.dndId}
                            onSelect={() => selectElement(child)}
                        />
                    ))}
                </SortableContext>
            );
        }
        return undefined;
    };

    /**
     * Renders the visibility badge for elements with visibility types
     * @returns {React.ReactNode} The visibility badge or null
     */
    const renderVisibilityBadge = () => {
        const visibilityTypes = 'visibilityTypes' in element ? element.visibilityTypes : undefined;
        if (visibilityTypes && visibilityTypes.length > 0) {
            return (
                <span 
                    title={`Visible in ${visibilityTypes.length} view(s)`}
                    style={{ marginLeft: 8 }}
                >
                    <EyeOutlined style={{ color: token.purple, fontSize: token.fontSize }} />
                    <span style={{ color: token.purple, fontSize: token.fontSize, marginLeft: 2 }}>
                        ({visibilityTypes.length})
                    </span>
                </span>
            );
        }
        return null;
    };

    /**
     * Renders the appropriate label for the element based on its type.
     * @returns {React.ReactNode} The rendered label or null.
     */
    const renderLabel = () => {
        if (element.uiElementType === UIElementType.Field) {
            const fieldElement = element as any;
            const ft = fieldTypes[fieldElement.fieldId];
            return (
                <Text type="secondary">
                    <Text>{ft?.description?.shortLabel || fieldElement.fieldId}</Text>
                    {fieldElement.required && ' *'}
                    {renderVisibilityBadge()}
                </Text>
            );
        } else if (element.uiElementType === UIElementType.EntityReference) {
            const entityElement = element as any;
            const entity = editor.entities.find(e => e.docId === entityElement.entityId);
            const entityLabel = entity?.description?.shortLabel || entityElement.entityId;

            if ((element as any).entityGroupId) {
                const entityGroupId = (element as any).entityGroupId;
                const group = entity?.recordGroups?.[entityGroupId];
                const groupLabel = group?.description?.shortLabel || entityGroupId;

                return (
                    <Text type="secondary">
                        <Text>{groupLabel}</Text>
                        {element.required && ' *'} <TeamOutlined />
                        {renderVisibilityBadge()}
                    </Text>
                );
            }

            return (
                <Text type="secondary">
                    <Text>{entityLabel}</Text>
                    {entityElement.required && ' *'}
                    {renderVisibilityBadge()}
                </Text>
            );
        } else if (element.uiElementType === UIElementType.EmbeddedEntity) {
            const entity = editor.entities.find(e => e.docId === element.entityId);
            const entityLabel = entity?.description?.shortLabel || element.entityId;

            if (element.entityGroupId) {
                const group = entity?.recordGroups?.[element.entityGroupId];
                const groupLabel = group?.description?.shortLabel || element.entityGroupId;

                return (
                    <Text type="secondary">
                        <Text>{groupLabel}</Text>
                        {element.required && ' *'} <BlockOutlined />
                        {renderVisibilityBadge()}
                    </Text>
                );
            }

            return (
                <Text type="secondary">
                    <Text>{entityLabel}</Text>
                    {element.required && ' *'} <ProfileOutlined />
                    {renderVisibilityBadge()}
                </Text>
            );
        } else if (element.uiElementType === UIElementType.FormProperty) {
            const formProp = element as UIFormProps;
            const propLabels = {
                formNumber: 'Form Number',
                formType: 'Form Type',
                formVersionNumber: 'Form Version Number',
            };
            return (
                <Text type="secondary">
                    <Text>{propLabels[formProp.id as keyof typeof propLabels] || formProp.id}</Text>
                    {' *'}
                    <FormOutlined style={{ marginLeft: 8, color: token.colorTextSecondary }} />
                    {renderVisibilityBadge()}
                </Text>
            );
        } else if (element.uiElementType === UIElementType.Attachments
            || element.uiElementType === UIElementType.ReferencedList) {
            const labelledElement = element as any;
            return (
                <Text type="secondary">
                    <Text>
                        {element.uiElementType === UIElementType.Attachments
                            ? 'Attachments'
                            : UIElementTypeDisplayNames.ReferencedList}
                    </Text>
                    {labelledElement.required && ' *'}
                    {renderVisibilityBadge()}
                </Text>
            );
        } else if (element.uiElementType === UIElementType.Button) {
            const buttonElement = element as UIButton;
            const buttonPreview = buttonElement.label
                || (buttonElement.menuSection ? workflowMenuSectionLabels[buttonElement.menuSection] : 'Workflow menu');
            return (
                <Text type="secondary">
                    <Text>{buttonPreview}</Text>
                    <ThunderboltOutlined style={{ marginLeft: 8, color: token.colorTextSecondary }} />
                </Text>
            );
        } else if (element.uiElementType === UIElementType.Table) {
            const tableElement = element as UIArTable;
            const tableType = tableTypes.find(t => t.docId === tableElement.tableId);
            const tableLabel = tableType?.description?.shortLabel || tableElement.tableId;
            return (
                <Text type="secondary">
                    <Text>{tableLabel}</Text>
                    {tableElement.required && ' *'}
                    <TableOutlined style={{ marginLeft: 8, color: token.colorTextSecondary }} />
                    {tableElement.columnConfig && (
                        <SettingOutlined style={{ marginLeft: 4, color: token.colorPrimary, fontSize: token.fontSizeSM }} />
                    )}
                    {renderVisibilityBadge()}
                    &nbsp;
                    <Text style={{ fontSize: token.fontSizeSM }}>
                        {tableElement.tableId}
                    </Text>
                </Text>
            );
        }
        return null;
    };

    /**
     * Defines the drop zone elements to be rendered based on element type.
     * All elements get 'before' and 'after' drop zones, containers also get an 'inside' drop zone.
     */
    const dropZones = [
        // Before drop zone for all elements
        {
            ref: beforeRef,
            id: `${element.dndId}-before`,
            style: {
                position: 'absolute',
                height: parentOrientation === 'HORIZONTAL' ? '100%' : (isContainer ? '25px' : '50%'),
                width: parentOrientation === 'VERTICAL' ? '100%' : (isContainer ? '25px' : '50%'),
                top: 0,
                left: 0,
                zIndex: 3,
                ...(over?.id === `${element.dndId}-before` ? {
                    backgroundColor: token['magenta'] + '45'
                } : {})
            }
        },
        // After drop zone for all elements
        {
            ref: afterRef,
            id: `${element.dndId}-after`,
            style: {
                position: 'absolute',
                height: parentOrientation === 'HORIZONTAL' ? '100%' : (isContainer ? '25px' : '50%'),
                width: parentOrientation === 'VERTICAL' ? '100%' : (isContainer ? '25px' : '50%'),
                bottom: parentOrientation === 'VERTICAL' ? 0 : undefined,
                right: parentOrientation === 'HORIZONTAL' ? 0 : undefined,
                top: parentOrientation === 'VERTICAL' && !isContainer ? '50%' : undefined,
                left: parentOrientation === 'HORIZONTAL' && !isContainer ? '50%' : 0,
                zIndex: 3,
                ...(over?.id === `${element.dndId}-after` ? {
                    backgroundColor: token['magenta'] + '45'
                } : {})
            }
        },
        // Inside drop zone for containers only
        ...(isContainer ? [{
            ref: insideRef,
            id: element.dndId,
            style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 2,
            }
        }] : [])
    ];

    return (
        <Popover
            content={
                <LayoutEditorControls 
                    element={element} 
                    parentElement={parentElement as EditorUIContainer | null} 
                />
            }
            open={isSelected}
            trigger="click"
            placement="bottom"
            arrow={false}
            onOpenChange={(visible) => {
                if (!visible && isSelected) {
                    selectElement(null);
                }
            }}
            styles={{ content: { 
                padding: 10,
                width: 320,
                backgroundColor: token.colorBgContainer,
            } }}
        >
            <div
                ref={setNodeRef}
                id={element.dndId}
                style={{
                    ...elementStyle,
                    position: 'relative',
                    ...(over?.id === element.dndId && {
                        border: '1px solid ' + token['magenta'] + '45',
                        backgroundColor: token['magenta'] + '45',
                    }),
                    ...(isDragging && {
                        opacity: 0.33,
                    }),
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect?.();
                }}
                {...attributes}
                {...listeners}
                onKeyDown={(e) => {
                    onActivateKey(e);
                    if (!e.isDefaultPrevented()) {
                        (listeners as { onKeyDown?: React.KeyboardEventHandler }).onKeyDown?.(e);
                    }
                }}
            >
                {dropZones.map(({ ref, id, style }) => (
                    <div
                        key={id}
                        ref={ref}
                        id={id}
                        style={style as React.CSSProperties}
                    />
                ))}

                {isContainer && (
                    <Text type="secondary" style={getTypeTextStyle()}>
                        {element.uiElementType.toUpperCase()}
                    </Text>
                )}
                {!isContainer && (
                    <Text>
                        {element.uiElementType === UIElementType.MetaField ? (
                            <>
                                {(element).id}
                                {' *'}
                                <InfoCircleOutlined style={getMetaIconStyle()} />
                                {renderVisibilityBadge()}
                                &nbsp;
                                <Text
                                    type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    {type}
                                </Text>
                            </>
                        ) : element.uiElementType === UIElementType.FormProperty ? (
                            <>
                                {renderLabel()}
                                &nbsp;
                                <Text
                                    type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    {type}
                                </Text>
                            </>
                        ) : element.uiElementType === UIElementType.Attachments
                            || element.uiElementType === UIElementType.ReferencedList ? (
                            <>
                                {renderLabel()}
                                &nbsp;
                                <Text
                                    type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    {type}
                                </Text>
                            </>
                        ) : element.uiElementType === UIElementType.Table ? (
                            <>
                                {renderLabel()}
                                &nbsp;
                                <Text
                                    type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    {type}
                                </Text>
                            </>
                        ) : (
                            <>
                                {renderLabel()}
                                &nbsp;
                                <Text
                                    type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    {type}
                                </Text>
                            </>
                        )}
                    </Text>
                )}
                {renderChildren()}

                <HolderOutlined style={getDragHandleStyle(isContainer, element.uiElementType === UIElementType.MetaField, element.uiElementType === UIElementType.Field)} />
            </div>
        </Popover>
    );
}; 